// src/components/common/Layout/MainLayout.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
  Collapse,
  Chip,
  Paper,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  CreditCard,
  AccountBalance,
  Person,
  Logout,
  Security,
  Receipt,
  Analytics,
  Support,
  ExpandLess,
  ExpandMore,
  PersonAdd,
  Business,
  Settings,
  Key,
  Warning,
  TrendingUp,
  LocalOffer,
  ReceiptLong,
  NoteAdd,
  AddCard,
  PendingActions,
  CloudUpload,
  Description,
  Book,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthService from '../../../services/authService';
import LimitService from '../../../services/limitService';
import CreditService from '../../../services/creditService';
import useRoleColors from '../../../hooks/useRoleColors';
import useResolvedUserId from '../../../hooks/useResolvedUserId';

const drawerWidth = 280;

// Navigation items based on user role
const getNavigationItems = (role) => {
  const commonItems = [
    { 
      text: 'Dashboard', 
      icon: <Dashboard />, 
      path: '/dashboard',
      roles: ['Admin', 'User'],
    },
  ];

  const roleBasedItems = {
    'Admin': [     
      { 
        text: 'User Management', 
        icon: <PersonAdd />, 
        path: '/admin/users',
        subItems: [
          { text: 'All Users', path: '/admin/users/all' },
        ],
      },
      { 
        text: 'Limit Management', 
        icon: <AccountBalance />, 
        path: '/admin/limits',
        subItems: [
          { text: 'Define Limit', path: '/admin/limits/define' },
          { text: 'View Limits', path: '/admin/limits/view' },
        ],
      },
      { 
        text: 'Pending Limit Requests', 
        icon: <PendingActions />, 
        path: '/admin/limits/pending',
        badgeCount: null, // Will be set dynamically
      },
      { 
        text: 'Loan Management', 
        icon: <CreditCard />, 
        path: '/admin/risks',
      },
      { 
        text: 'Credit Rates', 
        icon: <TrendingUp />, 
        path: '/admin/interest-rates',
      },
      { 
        text: 'Discount Management', 
        icon: <LocalOffer />, 
        path: '/admin/discounts',
      },
      { 
        text: 'Ledger', 
        icon: <Book />, 
        path: '/edefter',
        subItems: [
          { text: 'Dashboard', path: '/edefter/dashboard' },
          { text: 'Upload General Journal', path: '/edefter/upload' },
          { text: 'Trial Balance', path: '/edefter/trial-balance' },
          { text: 'Upload Trial Balance', path: '/edefter/mizan-reading' },
          { text: 'Add Company', path: '/edefter/add-company' },
        ],
      }
    ],
    'User': [
      { text: 'Payments', icon: <CreditCard />, path: '/user/credits' },
      { text: 'New Payment', icon: <AddCard />, path: '/user/new-credit' },
      { text: 'Receivables', icon: <PendingActions />, path: '/user/approvals' },
      { text: 'Discount Loans', icon: <ReceiptLong />, path: '/user/discount/list' },
      { text: 'New Discount Loans', icon: <NoteAdd />, path: '/user/discount/create' },
    ]
  };

  return [
    ...commonItems,
    ...(roleBasedItems[role] || []),
  ];
};

const MainLayout = ({ children, user = { name: 'John Doe', role: 'User', email: 'john@example.com' } }) => {
  const theme = useTheme();
  const currentUserId = useResolvedUserId(user);
  const {
    accent: accentColor,
    accentLight,
    accentGradient,
    accentGradientHover,
    softBg,
    softBgHover,
    contrast: accentContrast,
  } = useRoleColors();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [pendingLimitRequestsCount, setPendingLimitRequestsCount] = useState(0);
  const [pendingIncomingApprovalsCount, setPendingIncomingApprovalsCount] = useState(0);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
    handleProfileMenuClose();
  };

  const handleExpandClick = (itemText) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemText]: !prev[itemText]
    }));
  };

  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const fetchPendingIncomingApprovals = useCallback(async () => {
    if (user.role !== 'User' || !currentUserId) {
      setPendingIncomingApprovalsCount(0);
      return;
    }

    try {
      const result = await CreditService.getCreditsBySeller(currentUserId);
      if (result.success && Array.isArray(result.data)) {
        const pendingCount = result.data.filter(
          credit => (credit.status ?? '').toString().toLowerCase() === 'pending'
        ).length;
        setPendingIncomingApprovalsCount(pendingCount);
      }
    } catch (err) {
      console.error('Error fetching pending receivables count:', err);
    }
  }, [user.role, currentUserId]);

  // Fetch pending limit requests count (only for Admin users)
  useEffect(() => {
    if (user.role !== 'Admin') {
      return;
    }

    const fetchPendingCount = async () => {
      try {
        const result = await LimitService.getPendingRequests();
        if (result.success && result.data) {
          setPendingLimitRequestsCount(result.data.length || 0);
        }
      } catch (err) {
        console.error('Error fetching pending limit requests count:', err);
        // Silently fail - don't show error to user for badge count
      }
    };

    // Fetch immediately
    fetchPendingCount();

    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);

    return () => clearInterval(interval);
  }, [user.role]);

  // Fetch pending incoming approvals count (only for User role)
  useEffect(() => {
    if (user.role !== 'User' || !currentUserId) {
      return;
    }

    fetchPendingIncomingApprovals();
    const interval = setInterval(fetchPendingIncomingApprovals, 30000);

    return () => clearInterval(interval);
  }, [user.role, currentUserId, fetchPendingIncomingApprovals]);

  // Refresh incoming approvals count when visiting approvals page
  useEffect(() => {
    if (user.role === 'User' && location.pathname === '/user/approvals') {
      fetchPendingIncomingApprovals();
    }
  }, [user.role, location.pathname, fetchPendingIncomingApprovals]);

  // Also refresh count when navigating to/from pending requests page
  useEffect(() => {
    if (user.role === 'Admin' && location.pathname === '/admin/limits/pending') {
      const fetchPendingCount = async () => {
        try {
          const result = await LimitService.getPendingRequests();
          if (result.success && result.data) {
            setPendingLimitRequestsCount(result.data.length || 0);
          }
        } catch (err) {
          console.error('Error fetching pending limit requests count:', err);
        }
      };
      fetchPendingCount();
    }
  }, [location.pathname, user.role]);

  const SidebarHeader = () => (
    <Box 
      onClick={() => handleNavigation('/dashboard')}
      sx={{ 
        p: 3, 
        background: accentGradient,
        color: accentContrast,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          background: accentGradientHover,
          transform: 'scale(1.02)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
        }
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: 2,
            background: 'rgba(255,255,255,0.2)',
            mr: 1.5,
            border: '2px solid rgba(255,255,255,0.3)',
            overflow: 'hidden'
          }}>
            <img 
              src="/fidost_logo.png" 
              alt="DPG Finance"
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain',
                padding: '4px'
              }}
              onError={(e) => {
                // Fallback to icon if image fails to load
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
              }}
            />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              DPG Finance
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
              Digital Payment
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const NavigationList = () => (
    <List sx={{ px: 2, py: 2 }}>
      {getNavigationItems(user.role).map((item, index) => {
        // Check if this is the Pending Limit Requests item and has badge
        const isPendingLimitItem = item.text === 'Pending Limit Requests';
        const isIncomingApprovalsItem = item.text === 'Receivables';
        const showBadge = (isPendingLimitItem && pendingLimitRequestsCount > 0) ||
                          (isIncomingApprovalsItem && pendingIncomingApprovalsCount > 0);
        const badgeContent = isPendingLimitItem
          ? pendingLimitRequestsCount
          : isIncomingApprovalsItem
            ? pendingIncomingApprovalsCount
            : 0;
        
        return (
        <React.Fragment key={item.text}>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={isActiveRoute(item.path)}
              onClick={() => {
                if (item.subItems) {
                  handleExpandClick(item.text);
                } else {
                  handleNavigation(item.path);
                }
              }}
              sx={{
                minHeight: 48,
                borderRadius: 2,
                px: 2,
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  background: accentGradient,
                  color: accentContrast,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
                  '& .MuiListItemIcon-root': {
                    color: accentContrast,
                  },
                  '&:hover': {
                    background: accentGradientHover,
                  }
                },
                '&:hover': {
                  background: softBg,
                  transform: 'translateX(4px)',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {showBadge ? (
                  <Badge badgeContent={badgeContent} color="error" max={99}>
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                  fontSize: '0.9rem',
                  fontWeight: 500 
                }}
              />
              {item.subItems && (
                expandedItems[item.text] ? <ExpandLess /> : <ExpandMore />
              )}
            </ListItemButton>
          </ListItem>
          
          {item.subItems && (
            <Collapse in={expandedItems[item.text]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {item.subItems.map((subItem) => (
                  <ListItem key={subItem.text} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      selected={isActiveRoute(subItem.path)}
                      onClick={() => handleNavigation(subItem.path)}
                      sx={{ 
                        pl: 7,
                        pr: 2,
                        borderRadius: 2,
                        minHeight: 40,
                        transition: 'all 0.2s ease',
                        '&.Mui-selected': {
                          background: softBg,
                          color: accentColor,
                          fontWeight: 600,
                          borderLeft: '3px solid',
                          borderColor: accentColor,
                          '&:hover': {
                            background: softBgHover,
                          }
                        },
                        '&:hover': {
                          background: softBgHover,
                          transform: 'translateX(4px)',
                        }
                      }}
                    >
                      <ListItemText 
                        primary={subItem.text}
                        primaryTypographyProps={{ 
                          fontSize: '0.85rem',
                          fontWeight: isActiveRoute(subItem.path) ? 600 : 400
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          )}
        </React.Fragment>
        );
      })}
    </List>
  );

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: '#ffffff'
    }}>
      <SidebarHeader />
      <Divider />
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
        <NavigationList />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.primary'
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 70 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              background: softBg,
              '&:hover': {
                background: softBgHover,
              }
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: accentColor, lineHeight: 1 }}>
              {location.pathname === '/dashboard' ? 'Dashboard' : 
               location.pathname.includes('credits') && !location.pathname.includes('new-credit') ? 'Payments' :
               location.pathname.includes('new-credit') ? 'New Payment' :
               location.pathname.includes('approvals') ? 'Receivables' :
               location.pathname.includes('limits/define') ? 'Define Limit' :
               location.pathname.includes('limits/view') ? 'View Limits' :
               location.pathname.includes('limits/pending') ? 'Pending Limit Requests' :
               location.pathname.includes('limits') ? 'Limit Management' :
               location.pathname.includes('users') ? 'User Management' :
               location.pathname.includes('profile') ? 'Profile Settings' :
               location.pathname.includes('change-password') ? 'Change Password' :
               location.pathname.includes('invoice-approval') ? 'Upload New Invoice' :
               location.pathname.includes('/edefter/dashboard') ? 'Ledger Dashboard' :
               location.pathname.includes('/edefter/upload') ? 'Upload General Journal' :
               location.pathname.includes('/edefter/trial-balance') ? 'Trial Balance' :
               location.pathname.includes('/edefter/mizan-reading') ? 'Upload Trial Balance' :
               location.pathname.includes('/edefter/add-company') ? 'Add Company' :
               location.pathname.includes('/edefter') ? 'Edefter' :
               location.pathname.includes('/admin/risks') ? 'Loan Management' :
               location.pathname.includes('/admin/interest-rates') ? 'Credit Rates' :
               'DPG Finance'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
              {location.pathname === '/dashboard' ? 'Overview and quick actions' : 
               location.pathname.includes('credits') ? 'Manage your payments' :
               location.pathname.includes('approvals') ? 'Review and approve receivables' :
               location.pathname.includes('limits/pending') ? 'Review and approve pending limit requests from users' :
               location.pathname.includes('invoice-approval') ? 'Upload and review invoice documents' :
               location.pathname.includes('/edefter/dashboard') ? 'Ledger sales and marketing analytics dashboard' :
               location.pathname.includes('/edefter/upload') ? 'Upload general journal XML files' :
               location.pathname.includes('/edefter/trial-balance') ? 'Search and view trial balance reports by company and period' :
               location.pathname.includes('/edefter/mizan-reading') ? 'Upload and analyze trial balance files in Excel or PDF format' :
               location.pathname.includes('/edefter/add-company') ? 'Create a new company profile for trial balance uploads' :
               location.pathname.includes('/edefter') ? 'Ledger management and reporting' :
               'Welcome back'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Paper
              elevation={0}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                cursor: 'pointer',
                px: 1.5,
                py: 1,
                borderRadius: 2,
                borderColor: 'divider',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  borderColor: accentColor,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transform: 'translateY(-1px)'
                }
              }}
              onClick={handleProfileMenuOpen}
            >
              <Avatar sx={{ 
                width: 36, 
                height: 36,
                background: accentGradient,
                fontSize: '0.9rem',
                fontWeight: 600
              }}>
                {user.name && user.surname ? 
                  `${user.name.charAt(0)}${user.surname.charAt(0)}`.toUpperCase() : 
                  user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'text.primary' }}>
                  {user.name && user.surname ? `${user.name} ${user.surname}` : user.name || 'User'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                  {user.role || 'User'}
                </Typography>
              </Box>
              <ExpandMore sx={{ fontSize: 20, color: 'text.secondary' }} />
            </Paper>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            minWidth: 220,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {user.name && user.surname ? `${user.name} ${user.surname}` : user.name || 'User'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {user.email}
          </Typography>
        </Box>
        
        <MenuItem 
          onClick={() => handleNavigation('/profile')}
          sx={{ 
            py: 1.5,
            '&:hover': {
              background: softBg,
            }
          }}
        >
          <ListItemIcon><Person fontSize="small" color="primary" /></ListItemIcon>
          <Typography variant="body2">Profile Settings</Typography>
        </MenuItem>
        
        <MenuItem 
          onClick={() => handleNavigation('/change-password')}
          sx={{ 
            py: 1.5,
            '&:hover': {
              background: softBg,
            }
          }}
        >
          <ListItemIcon><Key fontSize="small" color="primary" /></ListItemIcon>
          <Typography variant="body2">Change Password</Typography>
        </MenuItem>
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItem 
          onClick={() => {
            handleProfileMenuClose();
            // Call logout immediately - it will redirect
            AuthService.logout(true);
          }}
          sx={{ 
            py: 1.5,
            color: 'error.main',
            '&:hover': {
              background: 'rgba(244, 67, 54, 0.08)',
            }
          }}
        >
          <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
              boxShadow: '4px 0 24px rgba(0,0,0,0.04)'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: '#f5f7fa',
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 70 } }} />
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;
